import { LOCAL_AGENT_PRESENCE_PORT } from './hooks/useLocalAgentPresence';

const PICK_URL = `http://127.0.0.1:${LOCAL_AGENT_PRESENCE_PORT}/v1/pick-folder`;

export type LocalAgentPickFolderResult =
  | { ok: true; path: string }
  | { ok: false; cancelled?: boolean; missing?: boolean; error: string };

/**
 * Ask the local nextcompli-agent (loopback) to open the OS folder chooser.
 * Browsers cannot expose real filesystem paths from <input type=file webkitdirectory>.
 */
export async function requestLocalAgentPickFolder(): Promise<LocalAgentPickFolderResult> {
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 120_000);
    const response = await fetch(PICK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      signal: controller.signal,
      cache: 'no-store',
    });
    window.clearTimeout(timer);
    const body = await response.json().catch(() => ({})) as {
      ok?: boolean;
      path?: string;
      error?: string;
    };
    if (response.status === 409 || /cancel/i.test(String(body.error || ''))) {
      return { ok: false, cancelled: true, error: String(body.error || 'cancelled') };
    }
    if (!response.ok || body.ok === false) {
      return {
        ok: false,
        error: String(body.error || `HTTP ${response.status}`),
      };
    }
    const path = String(body.path || '').trim();
    if (!path) {
      return { ok: false, error: 'No folder returned' };
    }
    return { ok: true, path };
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    if (name === 'AbortError') {
      return { ok: false, error: 'Folder picker timed out' };
    }
    return {
      ok: false,
      missing: true,
      error: 'Local agent not reachable. Install/update the agent on this computer, then try Browse again — or type ~/CODE.',
    };
  }
}
